/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DateTime } from '@easepick/bundle';
import { DatetimePicker, DatetimePickerProps, DatetimePickerResult } from '@squiz/sds';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LogsQuery } from '../../Logs.constants';
import { getDateRangeFromSearchParams } from '../../utils/GetDateRangeFromSearchParams/GetDateRangeFromSearchParams';
import { getLocalISOString } from '../../utils/GetLocalISOString/GetLocalISOString';
import { PRESET_TIME_CUSTOM } from '../SelectPresetTime/SelectPresetTime.constants';

/**
 * Filter logs by chosen datetime.
 */
export const FilterByDatetime = (): JSX.Element => {
  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  // Date params
  const searchDateRange = searchParams.get(LogsQuery.date.range) as string;
  const searchDateFrom = searchParams.get(LogsQuery.date.from) as string;
  const searchDateTo = searchParams.get(LogsQuery.date.to) as string;

  // Preset date
  const [presetDatetime, setPresetDatetime] = useState<DatetimePickerResult | null>(null);

  // Update search params based on DatetimePicker result
  const updateDateRange = (datetimeResult: DatetimePickerResult): void => {
    let shouldNavigate = false;

    const searchFrom = searchParams.get(LogsQuery.date.from) as string;
    const searchTo = searchParams.get(LogsQuery.date.to) as string;

    const resultFrom = getLocalISOString(new Date(datetimeResult.startDateTime));
    const resultTo = getLocalISOString(new Date(datetimeResult.endDateTime));

    if (searchFrom !== resultFrom) {
      shouldNavigate = true;
      searchParams.set(LogsQuery.date.from, resultFrom);
    }

    if (searchTo !== resultTo) {
      shouldNavigate = true;
      searchParams.set(LogsQuery.date.to, resultTo);
    }

    if (shouldNavigate) {
      setSearchParams(searchParams);
    }
  };

  // Derive the preset date range from current search params
  useEffect(() => {
    const [dateRange] = getDateRangeFromSearchParams(searchParams);
    setPresetDatetime(dateRange);

    // Only update DatetimePicker value if date params have changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDateFrom, searchDateTo]);

  // Handle switching to custom datetime
  useEffect(() => {
    const [dateRange, dateRangeError] = getDateRangeFromSearchParams(searchParams);

    // Reset the datetime when a non-custom range is used
    if (presetDatetime && searchDateRange !== PRESET_TIME_CUSTOM) {
      setPresetDatetime(null);
    }

    // Update search params when switching to a custom range
    if (!presetDatetime && !dateRangeError && searchDateRange === PRESET_TIME_CUSTOM) {
      updateDateRange(dateRange);
    }

    // Only update DatetimePicker value if core variables have changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetDatetime, searchDateRange, searchParams]);

  if (searchDateRange !== PRESET_TIME_CUSTOM) {
    return <></>;
  }

  // Logs are historical and kept for 14 days.
  // Limit the datetime picker to this range.
  const maxDate = new DateTime(new Date());
  const minDate = maxDate.clone().subtract(14, 'day');

  const pickerProps: DatetimePickerProps = {
    callback: updateDateRange,
    dateMaximum: maxDate,
    dateMinimum: minDate,
    presetEndDatetime: presetDatetime?.endDateTime,
    presetStartDatetime: presetDatetime?.startDateTime,
  };

  return (
    <div className="logs__datetime-picker">
      <div className="filter-logs__datetime">
        <DatetimePicker {...pickerProps} />
      </div>
    </div>
  );
};
